import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { toast } from 'react-toastify';

import {
  DetailField,
  EmptyState,
  LoadingState,
  PageHeader,
  PageLayout,
  SectionCard,
  StatusBadge,
} from '../../components/ui';
import {
  approveJobOffer,
  downloadJobOfferPdf,
  fetchJobOffer,
  rejectJobOffer,
  requestRevision,
  submitJobOffer,
} from '../../services/jobOffersApi';
import { buildBackendUrl } from '../../config/env';
import { createRoleCapabilities, parseStoredList } from '../../utils/roleCapabilities';

function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(value, decimals = 1) {
  if (value === null || value === undefined || value === '') return '—';
  return `${(Number(value) * 100).toFixed(decimals)}%`;
}

function fmtDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch (e) {
    return value;
  }
}

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return value;
  }
}

export default function JobOfferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionPending, setActionPending] = useState(null); // 'approve' | 'revision' | 'reject' | 'submit'
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // HR Admin can also have role=employee + actual_role=hr_admin (the same
  // shape as the HR team users), so go through createRoleCapabilities to
  // resolve hasHrAdminAccess instead of comparing the stored role string.
  const capabilities = useMemo(
    () => createRoleCapabilities({
      role: localStorage.getItem('role') || '',
      actualRole: localStorage.getItem('actual_role') || '',
      permissions: parseStoredList('permissions'),
      isPlatformAdmin: localStorage.getItem('is_platform_admin') === '1',
      userName: localStorage.getItem('name') || '',
      employeeCode: localStorage.getItem('employee_code') || '',
    }),
    [],
  );
  const isHrAdmin = capabilities.hasHrAdminAccess || capabilities.isPlatformAdmin;
  const employeeCode = (localStorage.getItem('employee_code') || '').trim();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJobOffer(id);
      setOffer(data);
    } catch (err) {
      console.error('Failed to load offer', err);
      setError(err?.response?.data?.message || 'Failed to load job offer.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const isAuthor = useMemo(
    () => offer?.created_by_employee_code && employeeCode && offer.created_by_employee_code === employeeCode,
    [offer, employeeCode],
  );

  const canEdit = useMemo(
    () => offer && ['draft', 'revision_requested'].includes(offer.status) && (isAuthor || isHrAdmin),
    [offer, isAuthor, isHrAdmin],
  );

  const canSubmit = useMemo(
    () => offer && ['draft', 'revision_requested'].includes(offer.status) && (isAuthor || isHrAdmin),
    [offer, isAuthor, isHrAdmin],
  );

  const canDecide = useMemo(() => offer?.status === 'pending_approval' && isHrAdmin, [offer, isHrAdmin]);

  const handleSubmit = async () => {
    if (!offer) return;
    setActionPending('submit');
    try {
      const updated = await submitJobOffer(offer.id);
      setOffer(updated);
      toast.success('Offer submitted for approval.');
    } catch (err) {
      console.error('Submit failed', err);
      toast.error(err?.response?.data?.message || 'Failed to submit offer.');
    } finally {
      setActionPending(null);
    }
  };

  const handleApprove = async () => {
    if (!offer) return;
    setActionPending('approve');
    try {
      const updated = await approveJobOffer(offer.id);
      setOffer(updated);
      toast.success('Offer approved with HR-admin signature.');
    } catch (err) {
      console.error('Approve failed', err);
      toast.error(err?.response?.data?.message || 'Failed to approve offer.');
    } finally {
      setActionPending(null);
    }
  };

  const handleRequestRevision = async () => {
    if (!offer || !revisionReason.trim()) return;
    setActionPending('revision');
    try {
      const updated = await requestRevision(offer.id, revisionReason.trim());
      setOffer(updated);
      toast.success('Revision requested.');
      setRevisionDialogOpen(false);
      setRevisionReason('');
    } catch (err) {
      console.error('Request revision failed', err);
      toast.error(err?.response?.data?.message || 'Failed to request revision.');
    } finally {
      setActionPending(null);
    }
  };

  const handleReject = async () => {
    if (!offer || !rejectionReason.trim()) return;
    setActionPending('reject');
    try {
      const updated = await rejectJobOffer(offer.id, rejectionReason.trim());
      setOffer(updated);
      toast.success('Offer rejected.');
      setRejectionDialogOpen(false);
      setRejectionReason('');
    } catch (err) {
      console.error('Reject failed', err);
      toast.error(err?.response?.data?.message || 'Failed to reject offer.');
    } finally {
      setActionPending(null);
    }
  };

  const handleDownloadPdf = async (variant = 'offer') => {
    try {
      await downloadJobOfferPdf(offer.id, variant, `${offer.offer_reference}-${variant}.pdf`);
    } catch (err) {
      console.error('Download failed', err);
      toast.error('Could not download PDF.');
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <LoadingState rows={10} header />
      </PageLayout>
    );
  }

  if (error || !offer) {
    return (
      <PageLayout>
        <EmptyState
          title="Could not load offer"
          description={error || 'Offer not found.'}
          actionLabel="Back to list"
          onAction={() => navigate('/hr/job-offers')}
        />
      </PageLayout>
    );
  }

  const calc = offer.calculation || {};
  const signaturePreview = offer.approver_signature_path
    ? buildBackendUrl(`/storage/${offer.approver_signature_path}`)
    : null;

  return (
    <PageLayout backgroundAccent>
      <PageHeader
        overline={offer.offer_reference}
        title={offer.candidate_name}
        subtitle={`${offer.position}${offer.project_name ? ' • ' + offer.project_name : ''}`}
        actions={(
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate('/hr/job-offers')}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfRoundedIcon />}
              onClick={() => handleDownloadPdf('offer')}
              disabled={offer.status === 'draft'}
            >
              Offer PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfRoundedIcon />}
              onClick={() => handleDownloadPdf('calculation')}
            >
              Calculation PDF
            </Button>
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={<EditRoundedIcon />}
                onClick={() => navigate(`/hr/job-offers/${offer.id}/edit`)}
              >
                Edit
              </Button>
            )}
          </Stack>
        )}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          {offer.status === 'revision_requested' && offer.revision_reason ? (
            <Alert severity="info" icon={<AutorenewRoundedIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Revision requested</Typography>
              <Typography variant="body2">{offer.revision_reason}</Typography>
            </Alert>
          ) : null}

          {offer.status === 'rejected' && offer.rejection_reason ? (
            <Alert severity="error" icon={<CancelRoundedIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Offer rejected</Typography>
              <Typography variant="body2">{offer.rejection_reason}</Typography>
            </Alert>
          ) : null}

          {offer.status === 'approved' ? (
            <Alert severity="success" icon={<CheckCircleRoundedIcon />} sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                Approved by {offer.approver_name || '—'}
                {offer.approved_at ? ` on ${fmtDateTime(offer.approved_at)}` : ''}
              </Typography>
            </Alert>
          ) : null}

          <SectionCard
            title="Status"
            action={<StatusBadge status={offer.status} />}
          >
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Reference" value={offer.offer_reference} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Created By" value={offer.created_by_name} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Submitted At" value={fmtDateTime(offer.submitted_at)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Approved At" value={fmtDateTime(offer.approved_at)} /></Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Candidate" sx={{ mt: 2 }}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Name (English)" value={offer.candidate_name} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Name (Arabic)" value={offer.candidate_name_ar || '—'} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Nationality (English)" value={offer.nationality} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Nationality (Arabic)" value={offer.nationality_ar || '—'} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><DetailField label="ID / Passport" value={offer.id_passport_no} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><DetailField label="Contact" value={offer.contact_no} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><DetailField label="Email" value={offer.email} /></Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Position & Contract" sx={{ mt: 2 }}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Position (English)" value={offer.position} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Position (Arabic)" value={offer.position_ar || '—'} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Project (English)" value={offer.project_name} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Project (Arabic)" value={offer.project_name_ar || '—'} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Project Post (English)" value={offer.project_post} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Project Post (Arabic)" value={offer.project_post_ar || '—'} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Location (English)" value={offer.location} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><DetailField label="Location (Arabic)" value={offer.location_ar || '—'} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Contract Status" value={offer.contract_status} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Contract Type" value={offer.contract_type} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Offer Date" value={fmtDate(offer.offer_date)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Validity (days)" value={offer.offer_validity_days} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Expected Joining" value={fmtDate(offer.expected_joining_date)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Annual Leave (days)" value={offer.annual_leave_days} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Probation (days)" value={offer.probation_days} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><DetailField label="Billing Rate %" value={fmtPct(offer.billing_rate_pct)} /></Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Salary Breakdown" subheader="Auto-calculated from the LOCAL CANDIDATE OFFER CALCULATION sheet" sx={{ mt: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Amount (SR / month)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow><TableCell>Basic + Area Allowance</TableCell><TableCell align="right">{fmtMoney(calc.basic_plus_area)}</TableCell></TableRow>
                  <TableRow><TableCell>Housing</TableCell><TableCell align="right">{fmtMoney(calc.housing)}</TableCell></TableRow>
                  <TableRow><TableCell>Transport</TableCell><TableCell align="right">{fmtMoney(calc.transport)}</TableCell></TableRow>
                  <TableRow><TableCell>Living Allowance</TableCell><TableCell align="right">{fmtMoney(calc.living_allowance)}</TableCell></TableRow>
                  <TableRow><TableCell>Ticket Allowance ({calc.ticket_allowance_type})</TableCell><TableCell align="right">{fmtMoney(calc.ticket_allowance)}</TableCell></TableRow>
                  <TableRow><TableCell>Make-Up Subsidy</TableCell><TableCell align="right">{fmtMoney(calc.makeup_subsidy)}</TableCell></TableRow>
                  <TableRow><TableCell>Other Allowance</TableCell><TableCell align="right">{fmtMoney(calc.other_allowance)}</TableCell></TableRow>
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}><TableCell>Subtotal Basic</TableCell><TableCell align="right">{fmtMoney(calc.subtotal_basic)}</TableCell></TableRow>
                  <TableRow><TableCell>Indemnity</TableCell><TableCell align="right">{fmtMoney(calc.indemnity)}</TableCell></TableRow>
                  <TableRow><TableCell>Leave</TableCell><TableCell align="right">{fmtMoney(calc.leave_cost)}</TableCell></TableRow>
                  <TableRow><TableCell>Iqama ({calc.iqama_type})</TableCell><TableCell align="right">{fmtMoney(calc.iqama_cost)}</TableCell></TableRow>
                  <TableRow><TableCell>Exit / Re-entry</TableCell><TableCell align="right">{fmtMoney(calc.exit_reentry)}</TableCell></TableRow>
                  <TableRow><TableCell>GOSI ({fmtPct(calc.gosi_pct)})</TableCell><TableCell align="right">{fmtMoney(calc.gosi)}</TableCell></TableRow>
                  <TableRow><TableCell>Health ({calc.health_persons} × {fmtMoney(calc.health_yearly_cost_per_person)}/yr)</TableCell><TableCell align="right">{fmtMoney(calc.health_insurance)}</TableCell></TableRow>
                  <TableRow><TableCell>Air Tickets</TableCell><TableCell align="right">{fmtMoney(calc.air_tickets)}</TableCell></TableRow>
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}><TableCell>Subtotal Required Overhead</TableCell><TableCell align="right">{fmtMoney(calc.subtotal_required_overhead)}</TableCell></TableRow>
                  <TableRow><TableCell>Agency Fees</TableCell><TableCell align="right">{fmtMoney(calc.agency_fees)}</TableCell></TableRow>
                  <TableRow><TableCell>Saudization</TableCell><TableCell align="right">{fmtMoney(calc.saudization)}</TableCell></TableRow>
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}><TableCell>Subtotal Optional Overhead</TableCell><TableCell align="right">{fmtMoney(calc.subtotal_optional_overhead)}</TableCell></TableRow>
                  <TableRow sx={{ '& td': { fontWeight: 700 } }}><TableCell>Subtotal Overhead</TableCell><TableCell align="right">{fmtMoney(calc.subtotal_overhead)}</TableCell></TableRow>
                  <TableRow sx={{ backgroundColor: 'success.light' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Total Monthly Package</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(calc.total_monthly_package)}</TableCell>
                  </TableRow>
                  <TableRow><TableCell>Billing Price</TableCell><TableCell align="right">{fmtMoney(calc.billing_price)}</TableCell></TableRow>
                  <TableRow><TableCell>Margin Profit</TableCell><TableCell align="right">{fmtMoney(calc.margin_profit)}</TableCell></TableRow>
                  <TableRow><TableCell>Profitability</TableCell><TableCell align="right">{fmtPct(calc.profitability_pct)}</TableCell></TableRow>
                  <TableRow><TableCell>Indemnity / Year</TableCell><TableCell align="right">{fmtMoney(calc.indemnity_per_year)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          {(offer.conditional_terms || offer.conditional_terms_ar || offer.notes) && (
            <SectionCard title="Notes" sx={{ mt: 2 }}>
              <Stack spacing={1.5}>
                {offer.conditional_terms && (
                  <DetailField label="Conditional Terms (English)" value={offer.conditional_terms} />
                )}
                {offer.conditional_terms_ar && (
                  <DetailField label="الشروط الإضافية (Arabic)" value={offer.conditional_terms_ar} />
                )}
                {offer.notes && (
                  <DetailField label="Internal Notes" value={offer.notes} />
                )}
              </Stack>
            </SectionCard>
          )}
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard title="Workflow Actions">
            <Stack spacing={1}>
              {canSubmit && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SendRoundedIcon />}
                  onClick={handleSubmit}
                  disabled={Boolean(actionPending)}
                >
                  {offer.status === 'revision_requested' ? 'Resubmit for Approval' : 'Submit for Approval'}
                </Button>
              )}
              {canDecide && (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleRoundedIcon />}
                    onClick={handleApprove}
                    disabled={Boolean(actionPending)}
                  >
                    Approve & Sign
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="info"
                    startIcon={<AutorenewRoundedIcon />}
                    onClick={() => setRevisionDialogOpen(true)}
                    disabled={Boolean(actionPending)}
                  >
                    Request Revision
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<CancelRoundedIcon />}
                    onClick={() => setRejectionDialogOpen(true)}
                    disabled={Boolean(actionPending)}
                  >
                    Reject
                  </Button>
                </>
              )}
              {!canSubmit && !canDecide && (
                <Typography variant="body2" color="text.secondary">
                  No actions available for this status.
                </Typography>
              )}
            </Stack>
          </SectionCard>

          {offer.status === 'approved' && (
            <SectionCard title="HR Admin Signature" sx={{ mt: 2 }}>
              <Stack spacing={1}>
                <DetailField label="Approved By (English)" value={offer.approver_name} />
                {offer.approver_name_ar ? (
                  <DetailField label="Approved By (Arabic)" value={offer.approver_name_ar} />
                ) : null}
                <DetailField label="Title (English)" value={offer.approver_title} />
                {offer.approver_title_ar ? (
                  <DetailField label="Title (Arabic)" value={offer.approver_title_ar} />
                ) : null}
                <DetailField label="Approved At" value={fmtDateTime(offer.approved_at)} />
                {signaturePreview ? (
                  <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
                    <img src={signaturePreview} alt="signature" style={{ maxHeight: 80 }} />
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Signature image will appear once the HR admin uploads one.
                  </Typography>
                )}
              </Stack>
            </SectionCard>
          )}

          <SectionCard title="History" sx={{ mt: 2 }}>
            <Stack spacing={1.25}>
              {(offer.revisions || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No history yet.</Typography>
              )}
              {(offer.revisions || []).map((entry) => (
                <Box key={entry.id} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                      {String(entry.action || '').replace(/_/g, ' ')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtDateTime(entry.created_at)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {entry.actor_name || '—'} · {entry.from_status || '—'} → {entry.to_status || '—'}
                  </Typography>
                  {entry.comment ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{entry.comment}</Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      <Dialog open={revisionDialogOpen} onClose={() => setRevisionDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request revision</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Tell the HR team what to fix before re-submission.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Revision reason"
            value={revisionReason}
            onChange={(e) => setRevisionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionDialogOpen(false)} disabled={actionPending === 'revision'}>Cancel</Button>
          <Button
            variant="contained"
            color="info"
            onClick={handleRequestRevision}
            disabled={actionPending === 'revision' || !revisionReason.trim()}
          >
            {actionPending === 'revision' ? 'Sending…' : 'Send Back for Revision'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectionDialogOpen} onClose={() => setRejectionDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject offer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This will close the offer permanently. State the reason for the record.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Rejection reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialogOpen(false)} disabled={actionPending === 'reject'}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionPending === 'reject' || !rejectionReason.trim()}
          >
            {actionPending === 'reject' ? 'Rejecting…' : 'Reject Offer'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
